import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { getPlayerMessages, markMessageAsRead, archiveMessage, markAllMessagesAsRead } from "@/lib/api/notifications";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MessageSquare, CheckCircle, Clock, User, Mail, Info, Archive, Search } from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import MessageDetailModal from "./MessageDetailModal";
import { PlayerMessage } from "@/lib/types";

export default function PlayerMessages() {
  const { toast } = useToast();
  const [selectedMessage, setSelectedMessage] = useState<PlayerMessage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch player messages
  const { data: messages, isLoading } = useQuery({
    queryKey: ["player-messages", { unread_only: unreadOnly }],
    queryFn: () => getPlayerMessages({ unread_only: unreadOnly }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Debug log to see what messages are returned
  console.log('Player messages response:', messages);
  console.log('Messages loading state:', isLoading);

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: markMessageAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-messages"] });
      queryClient.invalidateQueries({ queryKey: ["notification-counts"] });
    },
  });

  // Archive message mutation
  const archiveMutation = useMutation({
    mutationFn: archiveMessage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-messages"] });
      toast({
        title: "Message archived",
        description: "The message has been archived successfully",
      });
    },
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllMessagesAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-messages"] });
      queryClient.invalidateQueries({ queryKey: ["notification-counts"] });
      toast({
        title: "All messages marked as read",
        description: "All player messages have been marked as read",
      });
    },
  });

  const handleMessageClick = (message: PlayerMessage) => {
    setSelectedMessage(message);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    if (selectedMessage && !selectedMessage.is_read) {
      markAsReadMutation.mutate(selectedMessage.id);
    }
    setIsModalOpen(false);
    setSelectedMessage(null);
  };

  const handleArchive = (messageId: number) => {
    archiveMutation.mutate(messageId);
  };

  // Filter messages based on search query
  const filteredMessages = messages?.filter(message => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      message.players?.name?.toLowerCase().includes(query) ||
      message.subject.toLowerCase().includes(query) ||
      message.body_preview.toLowerCase().includes(query)
    );
  });

  const unreadCount = messages?.filter(m => !m.is_read).length || 0;

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Player Messages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Player Messages
              {unreadCount > 0 && (
                <Badge variant="destructive">{unreadCount} unread</Badge>
              )}
            </CardTitle>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isPending}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Mark All as Read
              </Button>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by player name, subject, or content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="unread-only"
                checked={unreadOnly}
                onCheckedChange={setUnreadOnly}
              />
              <Label htmlFor="unread-only">Unread only</Label>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!filteredMessages || filteredMessages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg">
              {searchQuery ? "No messages match your search" : 
               unreadOnly ? "No unread messages" : 
               "No player messages yet"}
            </p>
            <p className="text-sm mt-2">
              When players send follow-up emails, they'll appear here
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[600px] pr-4">
            <div className="space-y-4">
              {filteredMessages.map((message) => (
                <Card
                  key={message.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    !message.is_read ? 'border-cyan-500 bg-cyan-50/20' : ''
                  }`}
                  onClick={() => handleMessageClick(message)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-lg">
                              {message.players?.name || "Unknown Player"}
                            </h3>
                            {!message.is_read && (
                              <Badge variant="default" className="text-xs">New</Badge>
                            )}
                            {message.has_new_info && (
                              <Badge variant="secondary" className="text-xs">
                                <Info className="h-3 w-3 mr-1" />
                                Has updates
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchive(message.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                          <div className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {format(new Date(message.email_date), "MMM d, yyyy 'at' h:mm a")}
                          </div>
                          {message.players?.position && (
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {message.players.position.join(", ")}
                            </div>
                          )}
                        </div>

                        <h4 className="font-medium mb-1">{message.subject}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {message.body_preview}
                        </p>

                        <div className="flex items-center gap-4 mt-2">
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            Received {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                          </div>
                          {message.is_read && message.read_at && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <CheckCircle className="h-3 w-3" />
                              Read {formatDistanceToNow(new Date(message.read_at), { addSuffix: true })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>

      {/* Message Detail Modal */}
      {selectedMessage && (
        <MessageDetailModal
          message={selectedMessage}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onArchive={handleArchive}
        />
      )}
    </Card>
  );
}